import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth/hooks/useAuth';
import { searchTickerDirectory } from '../services/tickerApi';

const SEARCH_DEBOUNCE_MS = 500;
const SEARCH_MIN_LENGTH = 1;
const SEARCH_DEFAULT_LIMIT = 10;
const SEARCH_STALE_TIME_MS = 5 * 60 * 1000; // matches backend Redis TTL

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Debounced autocomplete search over the local ticker directory.
 * Pass the live input value; the hook debounces before calling the API.
 */
export function useTickerDirectorySearch(query: string, limit = SEARCH_DEFAULT_LIMIT) {
  const { session } = useAuth();
  const debouncedQuery = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const shouldSearch = debouncedQuery.length >= SEARCH_MIN_LENGTH;

  return useQuery({
    queryKey: ['ticker-directory-search', debouncedQuery, limit],
    queryFn: async () => {
      if (!session?.access_token) {
        return [];
      }
      return searchTickerDirectory({
        query: debouncedQuery,
        limit,
        token: session.access_token,
      });
    },
    enabled: shouldSearch && !!session?.access_token,
    staleTime: SEARCH_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    placeholderData: (previous) => previous,
  });
}
